import styled from '@emotion/styled';
import { debounce, flatten } from 'lodash';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import moment from 'moment';
import React, {
  createRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { CAL_V2_AGGREGATION_DATE_FORMAT } from '../../features/Experiences/pages/Details/CalendarV2/aggregate';
import { CalV2Item } from '../../features/Experiences/pages/Details/CalendarV2/types';
import { useCurrentUser } from '../../services/currentUser';

type SlidingCardGroup<T> = {
  cohortDate: string;
  items: T[];
};

export type SlidingContainer<T> = {
  date: string;
  groups: SlidingCardGroup<T>[];
};

const CARD_WIDTH = 300;
const CARD_MARGIN_RIGHT = 16;
const ARROW_CONTAINER_WIDTH = 48;

export const SLIDING_CARD_CONTAINER_HEIGHT = 500;

const Container = styled('div')((p) => ({
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: `${ARROW_CONTAINER_WIDTH}px 1fr ${ARROW_CONTAINER_WIDTH}px`,
  height: SLIDING_CARD_CONTAINER_HEIGHT,
  margin: `0 -${ARROW_CONTAINER_WIDTH}px`
}));

const CardsContainer = styled('div')`
  height: 100%;
  white-space: nowrap;
  overflow: auto;

  > :not(:last-child) {
    margin-right: ${(p) => p.theme.spacing(2)}px;
  }

  &::-webkit-scrollbar {
    background-color: ${(p) => p.theme.custom.colors.gray.white};
    width: ${(p) => p.theme.spacing()}px;
    height: ${(p) => p.theme.spacing()}px;
  }
  &:hover {
    &::-webkit-scrollbar-thumb {
      background-color: ${(p) => p.theme.custom.colors.gray.w300};
      border-radius: ${(p) => p.theme.custom.border.radius()}px;
      &:hover {
        background-color: ${(p) => p.theme.custom.colors.gray.w400};
      }
    }
  }
`;

const Card = styled('div')<{ isMostCurrentDate: boolean }>`
  display: inline-block;
  vertical-align: top;
  max-height: ${(p) => p.theme.spacing(60)}px;
  width: ${CARD_WIDTH}px;
  background-color: #ffffff;
  border: ${(p) =>
    p.isMostCurrentDate ? '1px solid #FFF1B8' : '1px solid #ebedef'};
  box-shadow: 0px 0px 16px 1px rgba(0, 0, 0, 0.07);
  border-radius: ${(p) => p.theme.custom.border.radius(1)}px;
  overflow: auto;

  &::-webkit-scrollbar {
    background-color: ${(p) => p.theme.custom.colors.gray.white};
    width: ${(p) => p.theme.spacing()}px;
    height: ${(p) => p.theme.spacing()}px;
  }
  &:hover {
    &::-webkit-scrollbar-thumb {
      background-color: ${(p) => p.theme.custom.colors.gray.w300};
      border-radius: ${(p) => p.theme.custom.border.radius()}px;
      &:hover {
        background-color: ${(p) => p.theme.custom.colors.gray.w400};
      }
    }
  }
`; // remove scrollbar on card - make it similar to container and only appear on hover;

const GroupSection = styled('div')((p) => ({
  padding: `${p.theme.spacing(2)}px ${p.theme.spacing(3)}px`
}));

const GroupItemSection = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: `${p.theme.spacing(1.5)}px 0`,
  cursor: 'pointer'
}));

const ExpansionIndicator = styled('span')`
  display: inline-block;
  width: 14px;
  font-size: 11px;
  color: ${(p) => p.theme.custom.colors.gray.w500};
`;

export const SlidingCardContainer = forwardRef<
  {
    scrollToStartingPosition: () => void;
  },
  {
    containers: SlidingContainer<CalV2Item>[];
    renderItem: (item: CalV2Item) => React.ReactNode;
    GroupContainerLabel: (groupLabel: string) => React.ReactNode;
    GroupItemLabel: (
      groupItemLabel: string,
      items: SlidingCardGroup<CalV2Item>['items']
    ) => React.ReactNode;
    onScrollHorizontallyWithinContainer: (
      // Indices of visible cards. positive integers indicate cards right of the starting card, negative values cards to its left.
      // e.g. as cards currently represent dates and we show the first current or future card in the starting position,
      // negative indices represent cards in the past, and positive indices represent cards in the future
      // The scrollDistance tells how much we shifted from the previous position.
      // E.g. -2 -> two cards to the left, or +3 -> two cards to the right
      leftMostCardIndex: number,
      rightMostCardIndex: number,
      scrollDistance: number
    ) => void;
    onScrollVerticallyWithinOneCard: (itemCountWithinCard: number) => void;
  }
>(
  (
    {
      containers,
      renderItem,
      GroupContainerLabel,
      GroupItemLabel,
      onScrollHorizontallyWithinContainer,
      onScrollVerticallyWithinOneCard
    },
    ref
  ) => {
    const [chevronVisibility, setChevronVisibility] = useState({
      left: false,
      right: false
    });
    // We don't want to trigger events when we initially position ourselves.
    // A useEffect scrolls the container to the right position - these scroll events we ignore.
    const trackScroll = useRef(false);
    // We want to keep track of the starting index, which is typically the next date.
    // This allows to communicate whether the user is looking into the past or into the future.
    const initialStartingIndex = useRef(0);
    // We try to keep track of what's currently shown, so that we only trigger the onScroll handler
    // when there's a meaningful change made.
    const visbibleCardsRef = useRef<{
      leftMostCardIndex: number;
      rightMostCardIndex: number;
    } | null>(null);
    const cardContainer = createRef<HTMLDivElement>();

    const scrollCardContainer = (x: number) => {
      if (!cardContainer.current) return;
      cardContainer.current.scrollBy({ top: 0, left: x, behavior: 'smooth' });
    };

    const updateChevronVisibility = () => {
      const el = cardContainer.current;
      if (!el) {
        return;
      }
      const nextLeft = el.scrollLeft > 0;
      const nextRight = el.scrollWidth > el.clientWidth + el.scrollLeft;
      if (
        nextLeft !== chevronVisibility.left ||
        nextRight !== chevronVisibility.right
      ) {
        setChevronVisibility({ left: nextLeft, right: nextRight });
      }
    };

    const computeStartingXCoordinate = (startingCardIndex: number) => {
      return (
        startingCardIndex * (CARD_WIDTH + CARD_MARGIN_RIGHT) -
        (startingCardIndex < containers.length ? 8 : 0)
      ); // move it a little to make it clearer that there's something to its left
    };

    useEffect(() => {
      trackScroll.current = false;
      const today = moment().format('YYYY-MM-DD');
      let nearestDate: string;
      let startingCardIndex: number = containers.length;

      containers.forEach((container, index) => {
        let diff = moment(container.date).diff(moment(today), 'days');
        if (diff >= 0) {
          if (nearestDate) {
            if (moment(container.date).diff(moment(nearestDate), 'days') < 0) {
              nearestDate = container.date;
              startingCardIndex = index;
            }
          } else {
            nearestDate = container.date;
            startingCardIndex = index;
          }
        }
      });
      const xCoordinate = computeStartingXCoordinate(startingCardIndex);
      scrollCardContainer(xCoordinate);
      initialStartingIndex.current = startingCardIndex;
      // arbitrary delay - just fairly long so that the initial page render will have taken place already
      setTimeout(() => (trackScroll.current = true), 2000);
      updateChevronVisibility();
    }, [containers]);

    //Imperative Handler
    useImperativeHandle(
      ref,
      () => {
        return {
          scrollToStartingPosition: () => {
            if (!cardContainer.current || !initialStartingIndex.current) return;
            const leftCoordinate = computeStartingXCoordinate(
              initialStartingIndex.current
            );
            cardContainer.current.scrollTo({
              top: 0,
              left: leftCoordinate,
              behavior: 'smooth'
            });
          }
        };
      },
      [cardContainer, containers]
    );

    const _onScrollContainer = (element: HTMLDivElement) => {
      updateChevronVisibility();
      const scrollLeft = element.scrollLeft;
      const clientWidth = element.clientWidth;
      // singleCardWidth could also be computed with Math.floor(scrollWidth / containers.length);
      // but this leads to very slight rounding problems. Better to rely on the hardcoded values
      const singleCardWidth = CARD_WIDTH + CARD_MARGIN_RIGHT;
      const leftMostCardIndex = Math.ceil(scrollLeft / singleCardWidth);
      // We don't care about the rightmost margin so we can add it here.
      // We also shift the whole index by 1, to account for the cardWidth of this element itself
      // There is a slight bug here which is not connected to this math per se, but to its surroundings. When
      // we reduce the browser window size, we sometimes get an x-axis scrollbar on the body. With a scrollbar
      // present the scrollLeft would also need to know the scrollLeft of the outer scrollbar. As we don't have
      // this value here it's possible that the rightMostCardIndex is too low. I'd say this is negligible for now.
      const rightMostCardIndex = Math.floor(
        (scrollLeft + clientWidth + CARD_MARGIN_RIGHT) / singleCardWidth - 1
      );

      // If the user is still looking at the same items, we don't fire a scroll event
      if (
        visbibleCardsRef.current?.leftMostCardIndex !== leftMostCardIndex ||
        visbibleCardsRef.current?.rightMostCardIndex !== rightMostCardIndex
      ) {
        const scrollDistance =
          leftMostCardIndex -
          (visbibleCardsRef.current?.leftMostCardIndex || 0);
        visbibleCardsRef.current = { leftMostCardIndex, rightMostCardIndex };

        if (trackScroll.current) {
          onScrollHorizontallyWithinContainer(
            leftMostCardIndex - initialStartingIndex.current,
            rightMostCardIndex - initialStartingIndex.current,
            scrollDistance
          );
        }
      }
    };
    const onScrollContainer = debounce(_onScrollContainer, 300);

    return (
      <>
        <Container>
          <ChevronLeft
            size={50}
            color={chevronVisibility.left ? 'darkGrey' : 'lightGrey'}
            onClick={() => {
              scrollCardContainer(-1 * (CARD_WIDTH + CARD_MARGIN_RIGHT));
            }}
            style={{ cursor: 'pointer' }}
          />
          <CardsContainer
            ref={cardContainer}
            onScroll={() => {
              const element = cardContainer.current;
              if (!element) {
                return;
              }
              onScrollContainer(element);
            }}
          >
            {containers.map((container) => {
              const mom = moment(
                container.date,
                CAL_V2_AGGREGATION_DATE_FORMAT
              );
              const isSameDate = mom.isSame(moment(), 'day');
              const count = flatten(container.groups).reduce(
                (acc: number, group: SlidingCardGroup<CalV2Item>) =>
                  acc + group.items.length,
                0
              );
              return (
                <Card
                  key={container.date}
                  isMostCurrentDate={isSameDate}
                  onScroll={() => onScrollVerticallyWithinOneCard(count)}
                >
                  {GroupContainerLabel(container.date)}
                  <GroupSection>
                    {container.groups.map((group) => {
                      return (
                        <GroupItem
                          key={group.cohortDate}
                          renderItem={renderItem}
                          group={group}
                          GroupItemLabel={GroupItemLabel}
                        />
                      );
                    })}
                  </GroupSection>
                </Card>
              );
            })}
          </CardsContainer>
          <ChevronRight
            size={50}
            color={chevronVisibility.right ? 'darkGrey' : 'lightGrey'}
            onClick={() => {
              scrollCardContainer(CARD_WIDTH + CARD_MARGIN_RIGHT);
            }}
            style={{ cursor: 'pointer' }}
          />
        </Container>
      </>
    );
  }
);

const GroupItem = <T extends unknown>({
  group,
  renderItem,
  GroupItemLabel
}: {
  group: SlidingCardGroup<T>;
  renderItem: (item: T) => React.ReactNode;
  GroupItemLabel: (
    groupItemLabel: string,
    items: SlidingCardGroup<T>['items']
  ) => React.ReactNode;
}) => {
  const [expandable, setExpandable] = useState<boolean>(true);
  const currentUser = useCurrentUser();
  const { org } = currentUser;

  return (
    <>
      <GroupItemSection onClick={() => setExpandable((prev) => !prev)}>
        <ExpansionIndicator>{expandable ? '▼' : '►'}</ExpansionIndicator>
        {GroupItemLabel(
          moment(group.cohortDate).format(`${org.dateFormats.MMDDYYYY}`),
          group.items
        )}
      </GroupItemSection>
      {expandable && <div>{group.items.map((item) => renderItem(item))}</div>}
    </>
  );
};
